// src/pages/LannaCraftsPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet"; // <--- เพิ่ม import
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import BackToTop from "../components/BackToTop";

const SECTIONS = [
  { id: "definition", title: "“ช่างล้านนา” หลักฐานจากเอกสารโบราณ" },
  { id: "cm-chronicle", title: "1) ตำนานพื้นเมืองเชียงใหม่" },
  { id: "ancient-law", title: "2) หนังสืออนุโลมญาณกฏหมายโบราณ" },
  { id: "ln-inscriptions", title: "3) จารึกล้านนา" },
  { id: "vessantara", title: "4) มหาชาติภาคพายัพ" },
  { id: "metteyya", title: "5) อนาคตวงส์–เมตเตยยสูตต์–เมตเตยยวงส์" },
  { id: "leb-loke", title: "6) ตำนานพระพุทธเจ้าเลียบโลก" },
  { id: "mangrai-law", title: "7) มังรายธรรมศาสตร์" },
  { id: "hatthakam", title: "8) หัตถกัมมวินิจฉัยฯ" },
  { id: "hongphakha", title: "9) โคลงหงส์ผาคำ" },
  { id: "lokanai", title: "10) โลกนัยชาดก" },
  { id: "yonok", title: "11) ตำนานโยนกฯ" },
  { id: "conclusion", title: "สรุปความสำคัญ" },
  { id: "bibliography", title: "บรรณานุกรม" },
];

function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const progress = (h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100;
      setPct(progress || 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-[55] h-1 bg-black/5">
      <div
        className="h-1 bg-[#d8653b] transition-[width] duration-150"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function ImageBlock({
  src = "/images/lanna/placeholder-1.jpg",
  alt = "placeholder image",
  caption,
  credit,
  ratio = "aspect-[16/9]",
}) {
  return (
    <figure className="my-6">
      <div className={`overflow-hidden rounded-xl ring-1 ring-black/10 bg-white ${ratio}`}>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-[1.03]"
        />
      </div>
      {(caption || credit) && (
        <figcaption className="mt-2 text-sm text-gray-600">
          {caption}
          {credit ? (
            <span className="block text-xs text-gray-500">
              เรียบเรียงโดย: สุคนธ์ทิพย์  จันทะลุน (บรรณารักษ์ชำนาญการ)
            </span>
          ) : null}
        </figcaption>
      )}
    </figure>
  );
}

function GalleryStrip({ images = [] }) {
  return (
    <div className="not-prose my-6 -mx-4">
      <div className="px-4 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none]">
        <div className="flex gap-4 py-1" style={{ scrollSnapType: "x mandatory" }}>
          {images.map((it, idx) => (
            <div
              key={idx}
              className="min-w-[74%] sm:min-w-[46%] lg:min-w-[32%]"
              style={{ scrollSnapAlign: "start" }}
            >
              <ImageBlock
                src={it.src}
                alt={it.alt}
                caption={it.caption}
                credit={it.credit}
                ratio={it.ratio || "aspect-[4/3]"}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuoteCard({ children }) {
  return (
    <div className="my-8 p-5 md:p-6 rounded-2xl bg-white shadow-sm ring-1 ring-black/5">
      <p className="text-lg md:text-xl leading-8 text-[#5b4a3e]">“{children}”</p>
    </div>
  );
}

function SectionDivider() {
  return (
    <div className="my-10">
      <svg viewBox="0 0 1440 80" className="w-full h-10 text-[#eadfce]">
        <path
          fill="currentColor"
          d="M0,32 C180,72 360,0 540,16 C720,32 900,96 1080,80 C1260,64 1440,16 1440,16 L1440,0 L0,0 Z"
          opacity="0.5"
        />
      </svg>
    </div>
  );
}

function PrevNext({ sections, active }) {
  const idx = sections.findIndex((s) => s.id === active);
  const prev = idx > 0 ? sections[idx - 1] : null;
  const next = idx < sections.length - 1 ? sections[idx + 1] : null;
  return (
    <div className="not-prose mt-8 flex flex-wrap gap-3">
      {prev && (
        <a
          href={`#${prev.id}`}
          className="px-4 py-2 rounded-xl border border-[#eadfce] bg-white text-[#5b4a3e] hover:border-[#d8653b] hover:text-[#d8653b] transition"
        >
          ← {prev.title}
        </a>
      )}
      {next && (
        <a
          href={`#${next.id}`}
          className="px-4 py-2 rounded-xl border border-[#eadfce] bg-white text-[#5b4a3e] hover:border-[#d8653b] hover:text-[#d8653b] transition"
        >
          {next.title} →
        </a>
      )}
    </div>
  );
}

/* ── TOC ตรงกลางหัวเว็บ (ส่งเป็น centerSlot ให้ SiteHeader) ── */
function HeaderTOC({ sections }) {
  const [value, setValue] = useState("");
  const onChange = (e) => {
    const id = e.target.value;
    setValue(id);
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div className="w-full max-w-[480px]">
      <label className="sr-only" htmlFor="toc-select">สารบัญ</label>
      <div className="relative">
        <select
          id="toc-select"
          value={value}
          onChange={onChange}
          className="w-full appearance-none px-3 sm:px-4 py-2.5 rounded-xl border border-[#eadfce] bg-white/90 text-[#5b4a3e]
                      shadow-sm focus:outline-none focus:ring-2 focus:ring-[#d8653b]/30"
        >
          <option value="">สารบัญ…</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>{s.title}</option>
          ))}
        </select>
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#5b4a3e]/60">▾</div>
      </div>
    </div>
  );
}

export default function LannaCraftsPage() {
  const [active, setActive] = useState("definition");
  const containerRef = useRef(null);

  // --- REACT-SNAP Logic ---
  useEffect(() => {
    // 1. สั่งหยุดการ Save ไว้ก่อน
    if (window.snapSaveState) {
      window.snapSaveState = () => {};
    }

    // 2. เนื่องจากหน้านี้เป็น Static Content (ไม่มี API)
    // เราแค่รอให้ Component Render และ Layout นิ่งสัก 500ms แล้วสั่ง Save เลย
    const timer = setTimeout(() => {
      if (window.snapSaveState) {
        window.snapSaveState = null;
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);
  // ------------------------

  // เลือก active section ตามการสกรอลล์ (จอใหญ่)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: [0, 1] }
    );
    const nodes = containerRef.current?.querySelectorAll("section[id]") ?? [];
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, []);

  const Anchor = ({ id, children }) => (
    <a
      href={`#${id}`}
      className={`block py-1.5 text-sm ${
        active === id ? "text-[#d8653b] font-medium" : "text-[#5b4a3e] hover:text-[#d8653b]"
      }`}
    >
      {children}
    </a>
  );

  return (
    <div className="bg-[#f8f6f3] text-[#5b4a3e]">
      {/* ===== Helmet (SEO) ===== */}
      <Helmet>
        <title>ช่างล้านนา : หลักฐานจากเอกสารโบราณ · Digital Library</title>
        <meta
          name="description"
          content="บทความวิชาการเรื่อง 'ช่างล้านนา' จากหลักฐานเอกสารโบราณ คัมภีร์ใบลาน ตำนาน และกฎหมายล้านนา เรียบเรียงโดย หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่"
        />
        <meta property="og:title" content="ช่างล้านนา : หลักฐานจากเอกสารโบราณ" />
        <meta
          property="og:description"
          content="ศึกษาประวัติศาสตร์และภูมิปัญญาช่างฝีมือล้านนาผ่านหลักฐานทางประวัติศาสตร์"
        />
        <meta property="og:image" content="/LannaCraftsPage/0.png" />
        <meta property="og:type" content="article" />
      </Helmet>

      <ReadingProgress />
      {/* ✅ ส่งสารบัญขึ้นไปแทรกตรงกลางระหว่างโลโก้กับเมนู */}
      <SiteHeader centerSlot={<HeaderTOC sections={SECTIONS} />} />

      {/* Hero: มีภาพ + เกรเดียนท์ทับ */}
      <section className="relative">
        <div
            className="absolute inset-0"
            style={{
            backgroundImage: "url('/LannaCraftsPage/0.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            }}
            aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#00000066] via-[#00000033] to-[#f8f6f3]/95" />

        <div className="relative max-w-6xl mx-auto px-4 py-16 lg:py-24">
            <div className="w-full md:w-3/4 lg:w-2/3">
            
                <div className="inline-block bg-white/50 backdrop-blur-sm rounded-xl px-4 py-3 shadow-md ring-1 ring-black/10">
                    <h1 className="text-[#5b3d1a] text-3xl font-bold">
                        ช่างล้านนา หลักฐานจากเอกสารโบราณ
                    </h1>
                    <p className="mt-2 text-[#4f4030]">
                        เรียบเรียงโดย สุคนธ์ทิพย์ จันทะลุน <br />
                        บรรณารักษ์ชำนาญการ   <br />
                      หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่
                    </p>
                </div>
            </div>
        </div>
        </section>



      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 py-8 lg:py-12">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* เนื้อหา */}
          <main className="lg:col-span-8">
            <article ref={containerRef} className="prose prose-lg max-w-none">

              {/* บทนำ + รูปใหญ่ */}
              {/* === แทนที่บล็อกเดิมทั้งหมดนี้ === */}
                <section id="definition" className="scroll-mt-24">
                {/* หัวเรื่องตรงกลาง */}
                <h2 className="!mt-0 text-center text-2xl md:text-3xl font-bold text-[#5b4a3e]">
                    ช่างล้านนา
                </h2>

                {/* ย่อหน้าเกริ่น (แก้ข้อความตามต้นฉบับของคุณได้) */}
                <p className="indent-8">
                    “ล้านนา” ดินแดนที่มีอาณาเขตอยู่บริเวณแถบภาคเหนือตอนบนของประเทศไทยซึ่งมีพื้นที่ครอบคลุมขอบเขต ๘ จังหวัด ได้แก่ เชียงราย เชียงใหม่ น่าน พะเยา แพร่ แม่ฮ่องสอน ลำปาง และลำพูน เป็นอาณาจักรที่มีความเป็นมาและประวัติศาสตร์อันยาวนาน ศูนย์กลางสำคัญของดินแดนล้านนานับจากอดีตถึงปัจจุบัน คือ เชียงใหม่ ซึ่งมีอายุมากกว่า ๗๐๐ ปี อาณาจักรล้านนาเป็นอาณาจักรไทยโบราณที่ถือว่าเป็นแหล่งอารยธรรมที่เก่าแก่แห่งหนึ่ง ด้วยพบหลักฐานวัตถุทางวัฒนธรรม ทั้งเครื่องมือเครื่องใช้และผลงานศิลปกรรมตามแหล่งโบราณคดีต่างๆ รวมถึงมีวัฒนธรรมและประเพณีพื้นเมืองที่สะท้อนให้เห็นถึงศิลปวัฒนธรรมที่เป็นเอกลักษณ์เฉพาะตัวของท้องถิ่น หรือที่เรียกว่า ศิลปวัฒนธรรมล้านนา ซึ่งถือกันว่าเป็นภูมิปัญญาของคนล้านนาที่สะท้อนถึงประเพณี วิถีชีวิต ความเชื่อ พิธีกรรม ดนตรีนาฏศิลป์ การแต่งกาย ศิลปะ ภาษาและวรรณกรรม (เกรียงไกร             เกิดศิริ และชาญคนิต อาวรณ์, ๒๕๕๔) ด้วยความเจริญรุ่งเรืองของศิลปวัฒนธรรมล้านนาจากอดีตกาลจนถึงปัจจุบัน เอกลักษณ์เฉพาะท้องถิ่นอย่างหนึ่งที่โดดเด่นและเป็นที่รู้จักอย่างแพร่หลายสืบทอดกันมาอย่างยาวนาน  ซึ่งนับว่าเป็นมรดกภูมิปัญญาทางวัฒนธรรมที่สำคัญอย่างหนึ่งของชาติคือ งานช่างล้านนา
                </p>

                {/* แถวภาพ 3 ภาพ (ใช้ไฟล์จาก public/assets/LannaCraftsPage/) */}
                <div className="not-prose my-5">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                    {/* รูปที่ 1 */}
                    <figure className="rounded-xl overflow-hidden ring-1 ring-black/10 bg-white aspect-[16/9]">
                        <img
                        src="/LannaCraftsPage/1.png"  // ← เปลี่ยนชื่อไฟล์ได้
                        alt="งานช่างล้านนา 1"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                        />
                    </figure>

                    {/* รูปที่ 2 */}
                    <figure className="rounded-xl overflow-hidden ring-1 ring-black/10 bg-white aspect-[16/9]">
                        <img
                        src="/LannaCraftsPage/2.png"  // ← เปลี่ยนชื่อไฟล์ได้
                        alt="งานช่างล้านนา 2"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                        />
                    </figure>

                    {/* รูปที่ 3 */}
                    <figure className="rounded-xl overflow-hidden ring-1 ring-black/10 bg-white aspect-[16/9]">
                        <img
                        src="/LannaCraftsPage/3.png"  // ← เปลี่ยนชื่อไฟล์ได้
                        alt="งานช่างล้านนา 3"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                        />
                    </figure>
                    </div>
                </div>

                {/* ย่อหน้าปิดท้าย (แก้ข้อความได้) */}
                <p className="indent-8">
                    “ช่าง” พจนานุกรมศัพท์ล้านนาเฉพาะคำที่ปรากฎในใบลาน (อรุณรัตน์ วิเชียรเขียว, ๒๕๓๙, น.๒๐๙)  ได้ให้ความหมายไว้ว่า ผู้ชำนาญในการช่างอย่างใดอย่างหนึ่ง ซึ่งสอดคล้องกับพจนานุกรมศัพท์ศิลปกรรม              ฉบับราชบัณฑิตยสถาน ได้กล่าวว่า ช่าง (น.) หมายถึง ผู้มีความชำนาญในการฝีมือหรืองานศิลปะอย่างใดอย่างหนึ่ง สามารถประดิษฐ์หรือสร้างสิ่งต่างๆ ขึ้นด้วยฝีมือและความชำนาญเฉพาะตน สิ่งที่สร้างขึ้นนั้นใช้ประโยชน์ได้ดี              และมีความสวยงามทำให้มีรูปแบบที่สอดคล้องกับประเพณีท้องถิ่น (ชาญคณิต อาวรณ์, ๒๕๕๒, น.๔๓)                    ในวัฒนธรรมล้านนาการเรียกผู้ที่มีความชำนาญการฝีมือหรือศิลปะต่างๆ ของล้านนานิยมเรียกว่า “ช่าง” นำหน้าชื่องานที่ผู้นั้นถนัด เช่น ช่างไม้ ช่างเหล็ก ช่างเงิน เป็นต้น ซึ่งตรงกับคำในภาษาพม่าว่า “สล่า” (สะหล่า) ที่มาจากคำว่า อาจารย์หรือช่าง ผู้ชำนาญการ ในสาขาวิชานั้นๆ ด้วย และกล่าวรวมถึงผู้ควบคุมการก่อสร้าง                       หรือการทำงานประเภทฝีมือนักดนตรี เช่น สล่ากอง สล่าแน สล่าสาน สล่าอย้อง และสล่าเค้า เป็นต้น (สารานุกรมวัฒนธรรมไทย ภาคเหนือ เล่ม ๔, ๒๕๔๒, น. ๖,๖๔๓ ; สมโชติ อ๋องสกุล, ๒๕๕๘, น.๒) 
                </p>
                <p className="indent-8">จากการศึกษาเอกสารโบราณทางประวัติศาสตร์ กฎหมาย ศาสนา ภาษาและวรรณกรรม ทั้งจารึก พงศาวดาร ตำนาน คัมภีร์ใบลาน และวรรณกรรมพื้นบ้าน ปรากฏหลักฐานว่ามีการกล่าวถึงวิชาชีพช่างและ               งานช่างฝีมือหลายแขนงของล้านนาโบราณ ดังนี้</p>
                </section>


              <SectionDivider />
                
                
              {/* 1-11: เนื้อหา + ภาพ + ปุ่มก่อนหน้า/ถัดไป */}
              <section id="cm-chronicle" className="scroll-mt-24">
                <h3><strong>๑. ตำนานพื้นเมืองเชียงใหม่ </strong></h3>
                <p className="indent-8">เป็นหลักฐานทางประวัติศาสตร์ที่กล่าวถึง ประวัติความเป็นมา วิวัฒนาการความเจริญรุ่งเรืองของอาณาจักรล้านนาที่มีเมืองเชียงใหม่เป็นศูนย์กลาง ซึ่งจากการตรวจสอบต้นฉบับใบลาน                ที่เป็นเอกสารข้อมูลปฐมภูมิ พบว่า ตำนานพื้นเมืองเชียงใหม่ ฉบับของวัดพระงาม เชียงใหม่ จารเมื่อ จ.ศ. ๑๒๑๖ (พ.ศ. ๒๔๙๗) จากหอสมุดแห่งชาติ และตำนานพื้นเมืองเชียงใหม่ ฉบับ CMA.HPms. จารเมื่อ จ.ศ. ๑๒๘๘             (พ.ศ. ๒๔๖๙) ของดร. ฮันธ์ เพนส์ เป็นเอกสารที่ทรงความสำคัญควบคู่กัน มีจำนวน ๘ ผูก โดยอาจารย์สงวน             โชติสุขรัตน์ ได้ปริวรรตจากอักษรธรรมล้านนาเป็นภาษาไทยกลาง และสำนักนายกรัฐมนตรีได้จัดพิมพ์เมื่อปี              พ.ศ. ๒๕๑๔ ปรากฎหลักฐานกล่าวถึง ช่างคล้องหรือช่างค้อง (ช่างทำฆ้อง, นักดนตรีผู้ตีฆ้อง) ช่างเครื่อง (ช่างทำเครื่องประดับเครื่องเงิน เครื่องทอง, ช่างทำอาวุธ) ช่างเงิน (ช่างทำเครื่องเงิน) ช่างคำ (ช่างทำทองคำ) และ             ช่างตีคำ, ช่างตีทอง, หรือช่างทอง (ช่างทำเครื่องทองเหลือง) </p>
                <ImageBlock src="/LannaCraftsPage/4.png" alt="ช่างราชสำนัก" caption={
                    <>
                    ภาพจิตรกรรมฝาผนังในวิหารลายคำ วัดพระสิงห์วรมหาวิหาร อ.เมือง จ.เชียงใหม่<br />
                    ซึ่งปรากฏภาพขบวนนักดนตรีผู้ตีฆ้อง<br />
                    (พิพิธภัณฑ์เสมือนสามมิติ, ๒๕๖๓)
                    </>
                } />
                <p className="indent-8">ดังข้อความตอนหนึ่งว่า “เพื่อหื้อเปนมิตรไมตรีติดต่อไพพายหน้าดีชะแล ว่าอั้นแล้วค็หื้อเสนาอามาจจ์จัดแจงเอาช่างคล้อง ช่างเครื่อง ช่างเงิน           ช่างคำ ช่างทอง ช่างเหล็ก ทังหลายประหมาณว่าได้ ๕๐๐ ครัว มาถวายแก่เจ้าพระญามังราย    เจ้ามังรายก็เอาช่างตีคำมาไว้เชียงตุง เอาช่างคล้องกับพวกหานบ้านมาไว้เชียงแสนเอาช่างเครื่อง ช่างเหล็ก ช่างทองมาไว้กุมกาม ช่างทังหลายจิ่งมากับบ้านเมืองเมืองล้านนาต่อเท้าบัดนี้แล”            
                (ตำนานพื้นเมืองเชียงใหม่, ๒๕๓๘, น.๓๐)
                </p>
              </section>
                <SectionDivider />
              <section id="ancient-law" className="scroll-mt-24">
                <h3><strong>๒. หนังสืออนุโลมญาณกฎหมายโบราณ ต้นฉบับวัดล้อมแรด อำเภอเถิน จังหวัดลำปาง</strong></h3>
                <p className="indent-8">เป็นกฎหมายล้านนาโบราณ รวบรวมหลักกฎหมายจากธรรมศาสตร์และราชศาสตร์โบราณของล้านนาที่ยังคงใช้ได้กับสังคมล้านนาในช่วงตรงกับรัชกาลของพระบาทสมเด็จพระจุลจอมเกล้าเจ้าอยู่หัว โดยอาจารย์สิงฆะ วรรณสัย และ ดร. ฮาร์ล ฮันดิอุช ได้ต้นฉบับมาจากวัดล้อมแรด อำเภอเถิน จังหวัดลำปาง และได้ถ่ายไมโครฟิล์มเก็บไว้ที่หอสมุดแห่งชาติ กรุงเทพมหานคร มีจำนวนทั้งหมด ๔๔ ใบลาน (ไม่มีชื่อผู้จารและศักราช) ซึ่งต่อมาได้ทำ           การปริวรรตจากคัมภีร์ใบลาน โดยอานนท์ กาญจนพันธ์ อรุณรัตน์ วิเชียรเขียว และ ศรีธน คำแปง และจัดพิมพ์เผยแพร่ในหนังสือปริวรรตจากคัมภีร์ใบลาน ชุดตำนานเมืองและกฎหมายล้านนา ลำดับที่ ๓ คณะสังคมศาสตร์ มหาวิทยาลัยเชียงใหม่ โดยปรากฎหลักฐานที่กล่าวถึง ช่างฟ้อรหรือช่างฟ้อน (นางรำ, นักฟ้อนรำ) ช่างซอ (นักขับเพลงซอ, นักขับร้องเพลงซอ) ช่างเอาท้ายเรือ (นายท้ายเรือ) ช่างแปงเรือ (นายช่างสร้างเรือ) ช่างตีละคุยหรือ ช่างชก (นักมวย) ช่างโหรา (โหร, หมอดู) ช่างแปลงช่อขาวสัตขาว (คนทำธงและฉัตรสีขาว) ช่างแปลงเริน             (ช่างสร้างบ้าน) ช่างแปลงรูป (ช่างปั้น) ช่างต้อง (ช่างแกะสลัก, ช่างทำลวดลาย) ช่างแปลงกองหรือช่างแปงกอง (ช่างทำกลอง) ช่างสว่า (ช่างทำฉาบ, นักบรรเลงฉาบ) ช่างธล้อหรือช่างธะล้อ (ช่างทำซอสะล้อ, คนสีสะล้อ)           ช่างเป่าคุ่ย (ช่างเป่าขลุ่ย, คนเป่าขลุ่ย) และช่างกลองต็อบหรือช่างกองตอบ (ช่างทำหรือนักเล่นกลองสองหน้าขนาดเล็ก) (อานันท์ กาญจนพันธ์ อรุณรัตน์ วิเชียรเขียว และศรีธน คำแปล, ๒๕๒๗)</p>
                <p className="indent-8">ดังข้อความในใบลานหน้าที่ ๓ ตอนหนึ่งว่า “ไนสิบสองคนนี้หื้ได้ตั้งเปนสักขีเทิอะ คนผู้บควรเป็นสักขีมีดังนี้แล คนผู้อันผิดราชวัตท้าวพระยานั้นนึ่ง คนมีพยาธิกับตัวนั้นนึ่ง เถ้าแก่เช่นนักนั้นนึ่ง ช่างฟ้อรนึ่ง ซ่างซอนึ่ง ช่างทองนึ่ง ช่างเหลกนึ่งคนอันท่านขับหนีออกจากเชื้อชาติยาติพี่น้องนึ่ง”</p>
                <div className="not-prose my-5">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                    {/* รูปที่ 1 */}
                    <figure className="rounded-xl overflow-hidden ring-1 ring-black/10 bg-white aspect-[16/9]">
                        <img
                        src="/LannaCraftsPage/5.png"  // ← เปลี่ยนชื่อไฟล์ได้
                        alt="งานช่างล้านนา 1"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                        />
                    </figure>

                    {/* รูปที่ 2 */}
                    <figure className="rounded-xl overflow-hidden ring-1 ring-black/10 bg-white aspect-[16/9]">
                        <img
                        src="/LannaCraftsPage/6.png"  // ← เปลี่ยนชื่อไฟล์ได้
                        alt="งานช่างล้านนา 2"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                        />
                    </figure>

                    {/* รูปที่ 3 */}
                    <figure className="rounded-xl overflow-hidden ring-1 ring-black/10 bg-white aspect-[16/9]">
                        <img
                        src="/LannaCraftsPage/7.png"  // ← เปลี่ยนชื่อไฟล์ได้
                        alt="งานช่างล้านนา 3"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                        />
                    </figure>
                    </div>
                </div>
                <p className="indent-8">ใบลานหน้าที่ ๙ ปรากฎข้อความว่า “ผิข้าฟันจ่าช้าง จ่ามล้า ช่างเอาท้ายเรือ ตายได้เสียเงิน ๕           ร้อยบาด ผิว่าข้าฟันช่างแปงเรือ ไม้ชักและช่างถากไม้ ช่างแต้มตายได้เสียเงิน ๕ ร้อยบาต ผิข้าช่างตีละคุย ตายได้เสียเงิน ๕ ร้อยบาต ผิว่าฟันช่างโหรา ผู้มีประยาตายไช้ข้า ๔ สิบคน” </p>
                <p className="indent-8">และใบลานหน้าที่ ๑๐ ตอนหนึ่งว่า “ผิข้าฟันช่างแปลงช่อขาวสัตขาว ช่างแปลงเรินตายได้เสียเงิน             ๕ ร้อยบาด ผิข้าฟันช่างแปลงรูป ช่างแต้ม ช่างต้องตายได้เสียเงิน ๔ ร้อยบาด ผิข้าฝันช่างแปลงกอง ช่างค้อง ช่างกองตอบตายได้เสียเงินสองร้อยบาดเป็นสองพัน ผิข้าฟันช่างสว่า ช่างธล้อ สีซอตายได้เสียเงินสองร้อยบาดเปนสองพัน ผิข้าช่างฟ้อรช่างตีกองช่างเป่าคุ่ย หื้ตายได้เสียเงินร้อยบาดเปนพันนึ่ง”</p>
              </section>
                <SectionDivider />
              <section id="ln-inscriptions" className="scroll-mt-24">
                <h3><strong>๓. จารึกล้านนา ภาค ๑ เล่ม ๑ จารึกจังหวัดเชียงราย น่าน พะเยา แพร่</strong> </h3>
                <p className="indent-8">เป็นหนังสือที่รวบรวมจารึกที่พบในบริเวณ ๔ จังหวัดของภาคเหนือตอนบนของประเทศไทย ได้แก่ เชียงราย น่าน พะเยา และแพร่                     ซึ่งศาสตราจารย์ ดร. ประเสริฐ ณ นคร และคณะ ได้ร่วมกันปริวรรตและชำระจารึกล้านนาที่ยังไม่ได้อ่านจัดพิมพ์เผยแพร่ ส่วนจารึกที่พิมพ์เผยแพร่แล้วก็นำมาตรวจสอบอีกครั้ง โดยมูลนิธิเจมส์ เอช ดับเบิ้ลยู ทอมสันป์                     ได้จัดพิมพ์ในวโรกาสสมเด็จพระเทพรัตนราชสุดาฯ สยามบรมราชกุมารี พระชนมายุครบ ๓ รอบ พุทธศักราช ๒๕๓๔ ซึ่งในจารึกเจ้ามหาวงศ์ฯ ซ่อมพระธาตุแช่แห้ง พ.ศ. ๒๓๘๙ ต้นฉบับเป็นอักษรธรรมล้านนา ปรากฏหลักฐานกล่าวถึง ช่างก่อ (ช่างก่อสร้างธาตุ) ช่างเหล็กหรือช่างเหลก (ช่างเหล็ก) และช่างเคิง หรือช่างเคิ่ง     (ช่างทำเครื่องประดับตกแต่ง) และช่างไม้ (ช่างถากไม้)</p>
                <ImageBlock src="/LannaCraftsPage/8.png" alt="บูรณะศาสนสถาน" caption={
                    <>
                    ภาพที่ ๕ จิตรกรรมฝาผนังวัดบวกครกหลวง ต.ท่าศาลา อ.เมือง จ.เชียงใหม่ <br />
                    ซึ่งปรากฎภาพของช่างถากไม้<br />
                    (บันทึกภาพวันที่ ๔ กรกฎาคม ๒๕๖๔)
                    </>
                } />
                <p className="indent-8">ดังปรากฎข้อความในจารึกด้านที่ ๑ ว่า           “วีศรีกัญญาขัตติยราชวงศา แลเสนาอำมาตย์           ซู่คน ท่านจึงมีพระราชอาชญาหาเอาตัว ช่างก่อ ช่างเหล็ก ช่างเคิง ช่างเงิน ช่างคำ ช่างไม้ทั้งหลาย เข้ามา แลได้นิมนต์ คณะสงฆ์เจ้าในจักรวรรดิเมิง               ทั้งมวลเข้ามาเมตตาปฏิสังขรณ์ สร้างแปลงซ่อมแซมยังพระมหาชินธาตุเจ้า ทั้งสองหลังจึงได้สร้าง”             (จารึกล้านนา ภาค ๑ เล่ม ๑ : จารึกจังหวัดเชียงราย 
                น่าน พะเยา แพร่, ๒๕๓๔, น.๘๒) 
                </p>
                <SectionDivider />
              </section>

              <section id="vessantara" className="scroll-mt-24">
                <h3><strong>๔. มหาชาติภาคพายัพ ฉบับสร้อยสังกร สำนวนเอก</strong></h3>
                <p className="indent-8">เป็นเรื่องราวเกี่ยวกับพระสัมมาสัมพุทธเมื่อครั้งเสวยพระชาติเป็นพระโพธิสัตว์ พระนามว่า “พระเวสสันดร” มีทั้งหมด ๑๓ กัณฑ์ รวม ๙๘๕ คาถา โดยธรรมมหาชาติหรือมหาเวสสันดรชาดก นิยมเทศน์ในเดือนยี่เป็งหรือประเพณีตั้งธรรม ได้มีการคัดเลือกและแก้ไขปรับปรุงต่อเติมให้เหมาะสมจากหลายฉบับ โดยพยายามรักษาภาษาโบราณไม่ให้ภาษาใหม่เข้ามาปะปน                     ซึ่งพระธรรมราชานุวัตร (ฟู อตตสิวเถระ) พระมหาเถระของชาวล้านนา สอบทานชำระมหาชาติภาคพายัพ              ฉบับสร้อยสังกร สำนวนเอก และจัดทำสำเร็จเมื่อปี พ.ศ. ๒๔๙๘ โดยเรื่องราวบางตอนได้กล่าวถึงช่างขีดลายลวง, ช่างขีดลายหรือช่างขีด (ช่างวาด, จิตรกร) ช่างแต้ม (ช่างวาด, ช่างเขียน) ช่างตีกลองหรือช่างตีกอง (คนตีกลอง) ช่างตี กลองคำ (พนักงานตีกลองของกษัตริย์) และช่างหุ้ม (พนักงานหุ้มกลอง, พนักงานแห่)</p>
                <p className="indent-8">ดังปรากฎหลักฐานว่า “ประดับแดงดำรายก้านกาบเหลื้อม มะมาบมีวรรณ ขะบานใบขันแนวนีด           จักหื้อช่างขีดลายลวง ลายดอกดวงเกี้ยวรอด บี้เบ้อสอดบินตอม เสาขอมประดับแก้ว เรืองร่ามแล้วด้วยคำแดง พองพรายแสงด้วยโกฎ งามสะโรดรังสี ประดับมณีตั้งแต่ง ทั้งสี่แจ่งจตุรา รูปเทวายืนถ้อยเถียบ ตีนจับเลียบ ประณมกร งามบวรชะแล้ม ลายช่างแต้ม สัพพะอันมี ทังรูปกินรีแอ่นฟ้อน รูปกินนอนย้อนตามหลัง”  (มหาชาติภาคพายัพ ฉบับสร้อยสังกร สำนวนเอก, ๒๔๙๘, น. ๒๖๔)  </p>
                <ImageBlock src="/LannaCraftsPage/9.png" alt="จิตรกรและกลองคำ" caption={
                    <>
                    ภาพที่ ๖ ภาพจิตรกรรมฝาผนังที่วัดทุ่งฝูง อ.วังเหนือ จ.ลำปาง  <br />
                    ปรากฏภาพคนตีกลองของวงดนตรี <br />
                    (สงกรานต์ สมจันทร์, ๒๕๕๖) 
                    </>
                } />
                <p className="indent-8">และข้อความตอนหนึ่งว่า“คนทั้งหลายฝูงช่างขับและช่างฟ้อน สังสรืดหย้อนตีนมือไว คนฝูงช่างตีกลอง  ใหหุ้มปาก มีหลายหลากยังยาย ช่างขับนิยายเล่นลวงโลก หื้อหายโศกมายาฯ มือนับเล่าสอนใจ เภรีใด             ดังด่วน หน้าใดม่วนผู้ตีไป คนฝูงใดช่างเป่าวุ่นและปัณเฑาะ และสังข์ติ่ง จักเข้ทะร้อ จุ่งหื้อตีต่อทางเดิน ช่างตีกลองคำกลองเงินและช่างหุ้ม จุ่งตีแต่ คุ้มเราไป” (มหาชาติภาคพายัพ ฉบับสร้อยสังกร 
                    สำนวนเอก, ๒๔๙๘, น. ๓๓๒) 
                </p>
                <SectionDivider />
              </section>

              <section id="metteyya" className="scroll-mt-24">
                <h3><strong>๕. อนาคตวงส์ เมตเตยยสูตต์ และเมตเตยยวงส์ สำนวนล้านนา </strong></h3>
                <p className="indent-8">เป็นเรื่องกล่าวถึงประวัติย่อของพระโพธิสัตว์ ผู้บำเพ็ญพระบารมีในชาติหนึ่ง ซึ่งปรากฏเป็นยอดปรมัตถบารมีอันประเสริฐเกิดสำเร็จผล ทรงพระอภินิหาร อุบัติตรัสเป็นพระสัมมาสัมพุทธเจ้าสิบพระองค์ในโลก ณ อนาคตกาลภายหน้านั้น ต้นฉบับใบลาน สถาบันวิจัยสังคม มหาวิทยาลัย เชียงใหม่ ได้ถ่ายไมโครฟิล์มไว้ จำนวนทั้งหมด ๗ ผูก ปริวรรตและตรวจชำระ             โดยบำเพ็ญ ระวิน ซึ่งได้ใช้เอกสารใบลานหลายสำนวน โดยใบลานจากวัดดอนไชย ระบุว่า คัดลอกเมื่อปี                  พ.ศ. ๒๔๗๖ ต่อมามหาจุฬาลงกรณราชวิทยาลัย วิทยาเขตเชียงใหม่ วัดสวนดอก เชียงใหม่ ได้จัดพิมพ์เผยแพร่                  เมื่อปี พ.ศ. ๒๕๓๕ โดยในอนาคตวงส์ เมตเตยยสูตต์ และเมตเตยยวงส์ ปรากฏหลักฐานกล่าวถึง ช่างทอหูก              (ช่างทอผ้า) และช่างสาน (ช่างจักสาน)</p>
                <ImageBlock src="/LannaCraftsPage/10.png" alt="ทอผ้าและสาน" caption={
                    <>
                    ภาพที่ ๗ การทอผ้าของผู้หญิงไทลื้อด้วยการทอหูกหรือกี่กระตุก  <br />
                    ภาพจิตรกรรมฝาผนัง วัดภูมินทร์ อ.เมือง จ. น่าน  <br />
                    (จักรพงษ์ คำบุญเรือง, ๒๕๖๐)  
                    </>
                }/>
                <p className="indent-8">ดังข้อความตอนหนึ่งในหน้า ๗๑ ว่า “ค็ยิ่งหื้อสุกอ่อนยิ่งนักแล้ว ค็ปั่นเปนเส้นฝ้ายแลยอ่อนสุขุมาลด้วยมือแห่งตน แล้วค็เลือกเอาช่างทอหูก  ทังหลายฝูงอันแกว่นเติย ชนาณทอผ้าดีนักมาแล้ว  ค็หื้อกินเข้าน้ำโภชนะอาหารอันประณีตดีแล้ว”</p>
                <p className="indent-8">และในหน้าที่ ๑๓๓ ความว่า “ดั่งจักได้ยินมานี้ ยังมีปุคคละ ๒ คน พ่อลูกอันเป็นช่างสาน อยู่ในเมืองพาราณสีค็มีในกาละเมื่อก่อนแล ยังมีในวัน ๑ ช่างสานพ่อลูกนั้น ค็ออกไพจากเวียงแล้วเข้าไพสู่ป่าไม้กลวง    (บำเพ็ญ ระวิน, ๒๕๓๕)</p>
                <SectionDivider />
              </section>

              <section id="leb-loke" className="scroll-mt-24">
                <h3><strong>๖. ตำนานพระพุทธเจ้าเลียบโลก </strong></h3>
                <p className="indent-8">วรรณกรรมทางพระพุทธศาสนาปรากฎแพร่หลายในทางภาคเหนือและภาคตะวันออกเฉียงเหนือของไทย เป็นเรื่องบันทึกการเดินทางของพระพุทธเจ้ามายังดินแดนสิบสองปันนา ล้านนา ล้านช้าง และอีสานของไทย ซึ่งเอกสารต้นฉบับใบลาน ตำนานพระเจ้าเลียบโลก ฉบับวัดกู่คำ อำเภอเมือง จังหวัดเชียงใหม่ จารด้วยอักษรธรรมล้านนา มีทั้งหมด ๑๒ ผูก จำนวนหน้าลาน ๔๗๐ หน้า ผูกที่ ๑ ถึงผูกที่ ๑๑ เป็นเรื่องของพุทธตำนานหรือตำนานพระธาตุพระบาทโดยสมบูรณ์ ส่วนเนื้อหาผูกที่ ๑๒ เป็นเรื่องย่อใจความในผูกที่ ๑ ถึงผูกที่ ๑๑ โดยปรากฎหลักฐานกล่าวถึง ช่างหม้อ (ช่างปั้นหม้อ, คนปั้นหม้อ)</p>
                <ImageBlock src="/LannaCraftsPage/11.png" alt="ช่างหม้อ" caption={
                    <>
                    ภาพที่ ๘ ภาพช่างปั้นหม้อชาวเชียงใหม่ จากหนังสือ An ASIAN ARCADY<br />
                    (May, R. le, 1926, p.141)    
                    </>
                }/>
                <p className="indent-8">ดังข้อความในผูกที่ ๙ ตอนหนึ่งว่า “จากนั้นพระพุทธเจ้าก็เสด็จไปสู่ทิศตะวันออกประมาณ ๑,๐๐๐ วา (บางฉบับว่า ๑,๐๐๐ วา) เสด็จไปถึงบ้านกุมภการเศรษฐี ยังมีลัวะผู้หนึ่งเป็นเศรษฐี ช่างหม้อ ปั้นหม้อขายและเป็นนายบ้านที่นั้น เห็นพระพุทธเจ้าเสด็จมาจึง          ทูลถาม “เจ้ากูจะไปที่ไหนหนอ” (ตำนานพระพุทธเจ้าเลียบโลก, ๒๕๕๐, น.๑๑๖)</p>
                <SectionDivider />
              </section>

              <section id="mangrai-law" className="scroll-mt-24">
                <h3><strong>๗. มังรายธรรมศาสตร์ </strong></h3>
                <p className="indent-8">ธรรมศาสตร์ เป็นเอกสารคัมภีร์ใบลาน จารด้วยอักษรธรรมล้านนา ต้นฉบับเป็นของวัดช้างค้ำ อำเภอเมือง จังหวัดน่าน สันนิษฐานว่าคัดลอกครั้งสุดท้ายในใบลานเมื่อปี พ.ศ. ๒๓๙๕ ซึ่งคัดลอก ลงสมุดโดยหนานอินทา เมืองพรหม ชาวจังหวัดน่าน เมื่อปี พ.ศ. ๒๕๑๕ จำนวน ๕๙ หน้ากระดาษ และปริวรรตโดยอรุณรัตน์ วิเชียรเขียว และแปลโดย Gehan Wijeyewardene ปัจจุบันต้นฉบับถ่ายเป็นไมโครฟิล์มเก็บรักษาไว้ที่ Richard Davis Collection, The Australian National University, Australia เป็นกฏหมายโบราณล้านนาฉบับหนึ่ง              ที่รวบรวมหลักกฎหมายจากธรรมศาสตร์และราชศาสตร์โบราณของล้านนา พระยามังรายโปรดให้ตราขึ้น เพื่อให้กษัตริย์องค์ต่อๆ ไป ได้ใช้เป็นบรรทัดฐานในการปกครองบ้านเมือง โดยปรากฏหลักฐานที่กล่าวถึง ช่างสน               (ช่างปัก, ช่างชุนผ้า)</p>
                <ImageBlock src="/LannaCraftsPage/12.png" alt="ช่างสน" caption={
                    <>
                    ภาพที่ ๙ ภาพการปักผ้าของชาวยอง เอกลักษณ์เฉพาะตัวของผ้าปักชาวยอง  <br />
                    จากงานท่องเที่ยวชุมชน ชมวิถีชีวิตคนยองสันทางหลวง บ้านสันทางหลวง อ.แม่จัน จ.เชียงราย<br />
                    (เชียงรายแต้แต้, ๒๕๖๑)  
                    </>
                }/>
                <p className="indent-8">ดังใจความตอนหนึ่งว่า “เสฏฐีผู้เปนสหาย             ไค่ได้ยปาดก่นถ่งลักเอาแก้วลูกค่าควรแสนคำไพแล้ว               จิงเอาแก้วควรค่าร้อยคำ ใส่ไว้ยถ่งแทนแล้ว หื้ช่างสน               ผู้สลาดสนไว้ยหื้ดีดังเกล่า เมื่อลุรเสฏฐีผู้เจ้าแก้วมาเอา  ไขดูแก้วบ่หันแก้วลูกค่าควรได้ยแสนคำนั้น  พ้อยหันแก้วลูกควรค่าได้ร้อยคำ นั้นมีไนถ่งหั้นแล คอยดูรอยจำจีค คยังดี ดังเก
                ล่ายินอสสจัน” (มังรายธรรมศาสตร์, ๒๕๒๖, น.๕๘)
                </p>
                <SectionDivider />
              </section>

              <section id="hatthakam" className="scroll-mt-24">
                <h3><strong>๘. หัตถกัมมวินิจฉัยบาฬีฏีการอมสมมุติราช</strong></h3>
                <p className="indent-8">เป็นกฎหมายล้านนาโบราณที่จารลงบนใบลานด้วยอักษรธรรมล้านนา ต้นฉบับของวัดศรีโคมคำ อำเภอเมือง จังหวัดพะเยา จารโดยวชิรปัญญา เมื่อปี พ.ศ. ๒๓๕๙               รวมหลักกฎหมายจากคัมภีร์พุทธศาสนาเป็นส่วนใหญ่ และผสมหลักกฏหมายจากแหล่งอื่นๆ เรื่องนี้มีคำบาลีตั้งเป็นหลักตลอดทั้งเรื่อง การตัดสินเปรียบเทียบทางโลกและทางธรรมอยู่เสมอ มีข้อความหลายตอนที่สมบูรณ์กว่ากฎหมายเรื่องอื่นๆ ตอนท้ายมีเรื่องสมมติราชา โดยสถาบันวิจัยสังคม มหาวิทยาลัยเชียงใหม่ ได้บันทึกไว้ในไมโครฟิล์ม จำนวน ๘๘ ใบลาน และจากนั้นปริวรรตโดยอรุณรัตน์ วิเชียรเขียว และศรีธน คำแปง ได้จัดพิมพ์เผยแพร่เมื่อปี พศ. ๒๕๒๗ ในหนังสือปริวรรตจากคัมภีร์ใบลาน ชุดตำนานเมืองและกฎหมายล้านนา ลำดับที่ ๔ คณะสังคมศาสตร์ มหาวิทยาลัยเชียงใหม่ โดยปรากฏหลักฐานกล่าวถึง ช่างหริน (ช่างอิฐ, ช่างปูน) และช่างแก้วแหวรเงินคำหรือช่างแก้วแหวนเงินคำ (ช่างทำเครื่องประดับ)</p>
                <ImageBlock src="/LannaCraftsPage/13.png" alt="อิฐปูนและประณีตศิลป์" caption={
                    <>
                    ภาพที่ ๑๐ ภาพช่างปูนปั้น เอกลักษณ์เฉพาะลวดลายแบบพื้นเมือง   <br />
                    บ้านแม่สาร ต.เวียงยอง อ.เมือง จ.ลำพูน (จักรพงษ์ คำบุญเรือง, ๒๕๖๑)  
                    </>
                }/>
                <p className="indent-8">ดังข้อความในใบลานหน้าที่ ๕๓ ตอนหนึ่งว่า “พระยาคํตั้งหื้ผู้รู้ยังวัชชิรสาตรหื้เปนช่างแก้วแหวร  เงินคำแล จิ่งตั้งยังคนผู้รู้กัมมกรณสาตรไว้ยหื้เปนช่างไม้ช่างหรินอันกะทำด้วยกำลังอันหึกหยาบ แล้วจิ่งตั้งไว้หื้คนทังหลายหื้ เป็นไหยเถิงแก่ถานันตระตามคุณ            แห่งเขา” (อานันท์ กาญจนพันธ์ อรุณรัตน์ วิเชียรเขียว 
                    และศรีธน คำแปล, ๒๕๒๗, น.๕๕)   
                </p>
                <SectionDivider />
              </section>

              <section id="hongphakha" className="scroll-mt-24">
                <h3><strong>๙. โคลงหงส์ผาคำ (หงษ์) </strong></h3>
                <p className="indent-8">ต้นฉบับเขียนด้วยอักษรธรรมล้านนา โดยต้นฉบับเดิมเป็นของพ่อหนานแสน บ้านในเวียงประตูเมืองเชียงใหม่ อำเภอเมืองเชียงใหม่ เป็นวรรณกรรมประเภทโคลงสี่สุภาพ โคลงสอง              และโคลงสาม จำนวน ๕๕๘ โคลง เป็นเรื่องราวในสุวรรณวงศ์ชาดก ชาดกนอกนิบาตหรือนิทานพื้นเมืองของล้านนา ซึ่งแต่งเป็นภาษาบาลี ประมาณพุทธศตวรรษที่ ๑๙-๒๐ (พ.ศ. ๑๙๐๐-๒๑๐๐) โครงการวิจัยและรวบรวมเอกสารล้านนาไทย ศูนย์คติชนวิทยาล้านนา วิทยาลัยครูเชียงใหม่ จัดพิมพ์เมื่อปี พ.ศ. ๒๕๑๕ จำนวน ๑๐๖ หน้า โคลงหงส์ผาคำ ปรากฎข้อความที่กล่าวถึง ช่างช้อย(ผู้ขับลำนำจากคำประพันธ์ประเภทคร่าวซอ) และ ช่างลาขัดเขิง (นักรำดาบ)</p>
                <ImageBlock src="/LannaCraftsPage/14.png" alt="ศิลป์การแสดง" caption={
                    <>
                    ภาพที่ ๑๑ ภาพนักฟ้อนดาบ ศิลปะการต่อสู้ของชาวล้านนาแต่โบราณ   <br />
                    โฮงเฮียนสืบสานล้านนา อ.เมือง จ.เชียงใหม่ (ฟ้อนดาบล้านนา, ๒๕๕๗)  
                    </>
                }/>
                <p className="indent-8">ดังความตอนหนึ่งว่า “นักคุณทังฝูงช่างช้อย      
                  ช่างซอเพิงฯ ทังหมู่ช่างฟ้อน ช่างลาขัดเขิง ฝูงแกว่นเชิง 
                  หื้อพากันไพต้อน” (มณี พยอมยงค์, ๒๕๑๕)
                </p>
                <SectionDivider />
              </section>

              <section id="lokanai" className="scroll-mt-24">
                <h3><strong>๑๐. โลกนัยชาดก </strong></h3>
                <p className="indent-8">เป็นวรรณคดีล้านนาไทยเรื่องหนึ่งในชาดกนอกนิบาตที่นักปราชญ์ล้านนาไทยโบราณ  ได้แต่งไว้เป็นภาษาบาลีและแปลเป็นภาษาล้านนาเพื่อเทศน์สอนพระราชาและประชาชนทั่วไป มีจำนวนทั้งหมด ๑๑ ผูก ตอนท้ายมีข้อความเขียนว่า “น้อยยะ” เป็นผู้จาร เมื่อจุลศักราช ๑๒๒๒              (พ.ศ. ๒๔๐๓) โดยสิงฆะ วรรณสัย ได้ปริวรรตจากต้นฉบับเดิมอักษรลานนา และหน่วยส่งเสริมศิลปและวัฒนธรรมลานนา                   คณะมนุษยศาสตร์ มหาวิทยาลัยเชียงใหม่ จัดพิมพ์เผยแพร่เมื่อปี           พ.ศ. ๒๕๒๒ โดยปรากฏหลักฐานกล่าวถึง ช่างปล้ำ (นักมวยปล้ำ) </p>
                <p className="indent-8">ดังข้อความตอนหนึ่งว่า “ส่วนอภัยยะนั้นมันเป็นลูก           ช่างปล้ำ ประกอบด้วยปฏิฆจิตใคร่จงเบียดเบียรท่านด้วยดัน 
                รบพุ่งแทงท้านนั้น เหตุนั้นมันก็สักเสิญในอันแพ้ยังท่านว่า
                เป็นใหญ่และประเสริฐ” (สิงฆะ วรรณสัย, ๒๕๒๒, น.๗๗)
                </p>
                <ImageBlock src="/LannaCraftsPage/15.png" alt="ช่างปล้ำ" caption={
                    <>
                    ภาพที่ ๑๒  ภาพท่ารัดคอของมวยปล้ำเขมร สันนิษฐานว่ามี
                    ลักษณะคล้ายกันกับมวยปล้ำไทยโบราณที่ดูเหมือนจะปัจจุบันจะสูญหายไปแล้ว
                    <br />
                    (กรกิจ ดิษฐาน, ๒๕๖๑)    
                    </>
                }/>
                <SectionDivider />
              </section>

              <section id="yonok" className="scroll-mt-24">
                <h3><strong>๑๑. ตำนานโยนกนาคนครชยบุรีศรีช้างแส่น </strong></h3>
                <p className="indent-8">เป็นเอกสารคัมภีร์ใบลาน ต้นฉบับใบลานเป็นของ           วัดร่ำเปิง ตำบลนางแล อำเภอเมือง จังหวัดเชียงราย จารด้วยอักษรธรรมล้านนา จำนวน ๗๐ ใบลาน โดยพระภิกษุพิมพิสาร เมื่อ จ.ศ. ๑๒๑๔ (พ.ศ. ๒๔๒๒) เนื้อหาสาระของตำนานเรื่องนี้ได้มีการคัดลอกและรวบรวมจัดพิมพ์ออกเผยแพร่โดยวัดในภาคเหนือเขตล้านนาแล้วหลายฉบับ ตำนานมีการกล่าวถึงประวัติพระพุทธเจ้าโคตมะ ได้เสด็จไปโปรดสัตว์ตามที่ต่างๆ รวมทั้งการเสด็จมาถึงเมืองโยนกนครราชธานีไชยบุรีศรีช้างแส่น หรือเมืองเชียงแสน                 โดยสงวน โชติสุขรัตน์ ได้เรียบเรียงจากต้นฉบับอักษรไทยยวน (ตัวเมือง) และเผยแพร่เป็นตำนานเรื่องหนึ่งในหนังสืออดีตกาลแห่งลานนาไทยและตำนานเมืองโยนกนครชัยบุรีศรีเชียงแสน ซึ่งได้กล่าวถึง ช่างของดอกไม้ หรือช่างของมาลาดอกไม้ (ช่างทำดอกไม้, ผู้ร้อยกรองดอกไม้)</p>
                <ImageBlock src="/LannaCraftsPage/16.png" alt="ของมาลาดอกไม้" caption={
                    <>
                    ภาพที่ ๑๓  ภาพคุณพิสิทธิ์ ไพโรจน์ ช่างดอกไม้และช่างร้อยมาลัยชาวจังหวัดลำพูน<br />
                    ผู้ร้อยพวงมาลัยช่อยาวขนาดเมตรกว่าที่ประณีตสวยงามถวายอนุสาวรีย์พระนางจามเทวี อ.เมือง จ.ลำพูน <br />
                    (พนิดา สงวนเสรีวานิช, ๒๕๖๑)     
                    </>
                }/>
                <p className="indent-8">ดังใจความตอนหนึ่งว่า “แล้วภายหลังท่านสร้างยังบุพพารามวิหาร” ที่พระพุทธเจ้าเคยมานั่งฉันข้าวบิณฑบาต ช่างของดอกไม้ หากให้ทานนั้นเป็นวิหาร ก่อสร้างพระพุทธเจ้าองค์ใหญ่แล้วใสรักหางประจิตรติดคำบริบูรณ์แล้วฯ” (สงวน โชติสุขรัตน์, ๒๕๑๒, น.๒๐๔)</p>
                <SectionDivider />
              </section>

              <section id="conclusion" className="scroll-mt-24">
                <h2><strong>สรุปความสำคัญ</strong></h2>
                <p className="indent-8">
                  “ช่างล้านนา” ถือเป็นวิชาชีพที่มีมาตั้งแต่โบราณ ดังจะเห็นได้จากหลักฐานที่ปรากฏในเอกสารโบราณ  ทั้งจารึก พงศาวดาร ตำนาน คัมภีร์ใบลาน และวรรณกรรมพื้นบ้านมากมาย ซึ่งสะท้อนให้เห็นถึงความสำคัญและคุณค่าของช่างในสังคมล้านนาจากอดีตจนถึงปัจจุบัน นับเป็นมรดกภูมิปัญญาทางวัฒนธรรมอันเป็นสมบัติล้ำค่า               ที่บรรพบุรุษได้สร้างสรรค์ สั่งสม และสืบทอดมาถึงคนรุ่นต่อรุ่น ในปัจจุบันช่างล้านนาในบางสาขาก็ยังคงสืบทอดกันต่อมาให้คงอยู่ หากแต่ช่างล้านนาบางสาขาก็ได้เลือนหายไปตามกาลเวลา จึงควรตระหนักช่วยกันสืบทอดและอนุรักษ์ให้องค์ความรู้และภูมิปัญญาของช่างล้านนาทั้งในอดีตและปัจจุบันนี้ดำรงอยู่อย่างยั่งยืน เพราะช่างล้านนาไม่ได้แค่อาชีพของคนในท้องถิ่นเท่านั้น หากแต่ยังเป็นองค์ความรู้และภูมิปัญญาดั้งเดิมที่สะท้อนอัตลักษณ์ทางวัฒนธรรมเฉพาะของท้องถิ่นและประเทศชาติด้วยเช่นกัน
                </p>
                <section id="conclusion" className="scroll-mt-24">
            
                <div className="my-6">
                    <h3 className="text-xl font-semibold text-[#5b4a3e] mb-3">
                    หนังสือเอกสารโบราณที่ปรากฏหลักฐานของ “ช่างล้านนา”
                    </h3>

                    {/* ✅ แสดงภาพแบบกริดเหมือนในภาพแนบ */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {[
                        "/LannaCraftsPage/17.png",
                        "/LannaCraftsPage/18.png",
                        "/LannaCraftsPage/19.png",
                        "/LannaCraftsPage/20.png",
                        "/LannaCraftsPage/21.png",
                        "/LannaCraftsPage/22.png",
                        "/LannaCraftsPage/23.png",
                        "/LannaCraftsPage/24.png",
                        "/LannaCraftsPage/25.png",
                        "/LannaCraftsPage/26.png",
                    ].map((src, i) => (
                        <div
                        key={i}
                        className="overflow-hidden rounded-lg bg-white ring-1 ring-black/10 shadow-sm"
                        >
                        <img
                            src={src}
                            alt={`หนังสือ ${i + 1}`}
                            className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-500"
                            loading="lazy"
                        />
                        </div>
                    ))}
                    </div>

                    {/* คำบรรยายใต้ภาพรวม */}
                    <p className="mt-4 text-sm text-center text-gray-600 leading-relaxed">
                    หนังสือเอกสารโบราณที่เป็นแหล่งอ้างอิงของ “ช่างล้านนา”  
                    จัดแสดงโดย หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่
                    </p>
                </div>
                </section>

              </section>

              <section id="bibliography" className="scroll-mt-24 text-[#4f4030] leading-relaxed">
                <h2 className="text-2xl font-semibold text-[#5b4a3e] mb-4">บรรณานุกรม</h2>

                <div className="space-y-3 text-sm md:text-base">
                    <p className="indent-8">
                    เกรียงไกร เกิดศิริ และชาญคณิต อาวรณ์. <em>ผ่อล้านนา มองวิถีวัฒนธรรมล้านนาโดยสังเขป.</em> กรุงเทพฯ:
                    อุษาคเนย์, ๒๕๕๔.
                    </p>
                    <p className="indent-8">
                    จารึกล้านนา ภาค ๑ เล่ม ๑ จารึกจังหวัดเชียงราย น่าน พะเยา แพร่. กรุงเทพฯ: มูลนิธิเจมส์ เอช ดับเบิ้ลยู ทอมป์สัน, ๒๕๓๔.
                    </p>
                    <p className="indent-8">
                    ชาญคณิต อาวรณ์. “ช่างในวัฒนธรรมล้านนา : จากหลักฐานจารึกและเอกสารทางประวัติศาสตร์”
                    <em>นิตยสารกรมศิลปากร</em> ๕๒, ๒ (มีนาคม – เมษายน ๒๕๕๒): ๔๓–๕๑.
                    </p>
                    <p className="indent-8">
                    <em>ตำนานพระพุทธเจ้าเลียบโลก.</em> เชียงใหม่: สมพรการพิมพ์, ๒๕๔๑.
                    </p>
                    <p className="indent-8">
                    <em>ตำนานพื้นเมืองเชียงใหม่ ฉบับเชียงใหม่ ๗๐๐ ปี.</em> เชียงใหม่: ศูนย์วัฒนธรรมจังหวัดเชียงใหม่
                    สถาบันราชภัฏเชียงใหม่, ๒๕๓๘.
                    </p>
                    <p className="indent-8">
                    บำเพ็ญ ระวิน. <em>อนาคตวงส์ เมตเตยยสูตต์ และเมตเตยยวงส์ สำนวนล้านนา.</em> กรุงเทพฯ:
                    มหาจุฬาลงกรณราชวิทยาลัย, ๒๕๓๕.
                    </p>
                    <p className="indent-8">
                    มณี พยอมยงค์. <em>โคลงหงส์ผาคำ.</em> เชียงใหม่: โครงการวิจัยและรวบรวมเอกสารลานนาไทย ศูนย์คติชนวิทยา
                    ลานนาไทย วิทยาลัยครูเชียงใหม่, ๒๔๙๘.
                    </p>
                    <p className="indent-8">
                    <em>มหาชาติภาคพายัพ ฉบับสร้อยสังกร สำนวนเอก.</em> กรุงเทพฯ: สำนักงาน ส. ธรรมภักดี, ๒๔๙๘.
                    </p>
                    <p className="indent-8">
                    <em>มังรายธรรมศาสตร์.</em> เชียงใหม่: [ม.ป.พ.], ๒๕๒๖.
                    </p>
                    <p className="indent-8">
                    สงวน โชติสุขรัตน์. <em>ตำนานเมืองโยนกชัยบุรีศรีเชียงแสน ในอดีตกาลแห่งลานนาไทยและตำนานเมืองโยนกนครชัยบุรีศรีเชียงแสน.</em>
                    เชียงใหม่: สงวนการพิมพ์, ๒๕๑๒.
                    </p>
                    <p className="indent-8">
                    สมโชติ อ๋องสกุล. <em>ชุมชนช่างในเวียงเชียงใหม่ : ประวัติศาสตร์ชุมชน.</em> เชียงใหม่: ศูนย์ล้านนาศึกษา, ๒๕๕๘.
                    </p>
                    <p className="indent-8">
                    <em>สารานุกรมวัฒนธรรมไทย ภาคเหนือ เล่ม ๔.</em> กรุงเทพฯ: มูลนิธิสารานุกรมวัฒนธรรมไทย ธนาคารไทยพาณิชย์, ๒๕๔๒.
                    </p>
                    <p className="indent-8">
                    สิงฆะ วรรณสัย. <em>โลกนัยชาดก.</em> เชียงใหม่: หน่วยส่งเสริมศิลปศึกษาและวัฒนธรรมลานนา
                    คณะมนุษยศาสตร์ มหาวิทยาลัยเชียงใหม่, ๒๕๒๔.
                    </p>
                    <p className="indent-8">
                    อรุณรัตน์ วิเชียรเขียว. <em>พจนานุกรมศัพท์ล้านนาเฉพาะคำที่ปรากฎในใบลาน.</em> เชียงใหม่: สุริวงศ์บุ๊คเซนเตอร์, ๒๕๓๙.
                    </p>
                    <p className="indent-8">
                    อานันท์ กาญจนพันธ์, อรุณรัตน์ วิเชียรเขียว และศรีธน คำแปล. <em>หนังสืออนุโลมญาณกฎหมายโบราณ...</em>
                    เชียงใหม่: คณะสังคมศาสตร์ มหาวิทยาลัยเชียงใหม่, ๒๕๒๗.
                    </p>
                </div>

                <h3 className="text-xl font-semibold text-[#5b4a3e] mt-10 mb-3">บรรณานุกรม (ภาพประกอบ)</h3>

                <div className="space-y-3 text-sm md:text-base">
                    <p className="indent-8">
                    กรกิจ ดิษฐาน. <em>มวยปล้ำไทยคืออะไร?</em> [ออนไลน์]. สืบค้นข้อมูลเมื่อ ๒๔ กรกฎาคม ๒๕๖๔ จาก
                    <a href="https://www.gypzyworld.com/article/view/1207" target="_blank" className="text-[#d8653b] hover:underline">
                        {" "}https://www.gypzyworld.com/article/view/1207
                    </a>
                    </p>
                    <p className="indent-8">
                    ไกรวุฒิ เมืองพาน. [ออนไลน์]. <em>ช่างปี่กำลัง "หยุบนม" ช่างซอ ภาพจิตรกรรมฝาผนังวัดเจริญเมือง</em> อำเภอพาน จังหวัดเชียงราย.
                    สืบค้นเมื่อ ๒๑ กรกฎาคม ๒๕๖๔ จาก{" "}
                    <a href="https://www.facebook.com/TheLannaMusic/photos/a.825290924186646/826436244072114/" target="_blank" className="text-[#d8653b] hover:underline">
                        Facebook: The Lanna Music
                    </a>
                    </p>
                    <p className="indent-8">
                    จักรพงษ์ คำบุญเรือง. [ออนไลน์]. <em>จิตรกรรม “วัดภูมินทร์” ความงามของศิลปช่างสกุลล้านนา.</em> สืบค้นเมื่อ ๑๒ กรกฎาคม ๒๕๖๔ จาก{" "}
                    <a href="https://www.chiangmainews.co.th/page/archives/632716/" target="_blank" className="text-[#d8653b] hover:underline">
                        Chiang Mai News
                    </a>
                    </p>
                    <p className="indent-8">
                    _____________. <em>วิถีชาวยองบ้านแม่สาร หญิงทอผ้า ชานปั้นปูน.</em> [ออนไลน์]. สืบค้นเมื่อ ๒๔ กรกฎาคม ๒๕๖๔ จาก{" "}
                    <a href="https://www.chiangmainews.co.th/page/archives/794191/" target="_blank" className="text-[#d8653b] hover:underline">
                        Chiang Mai News
                    </a>
                    </p>
                    <p className="indent-8">
                    พิพิธภัณฑ์เสมือนสามมิติ. [ออนไลน์]. <em>ภาพจิตรกรรมฝาผนัง ในวิหารลายคำ วัดพระสิงห์วรมหาวิหาร.</em> สืบค้นเมื่อ ๑๐ กรกฎาคม ๒๕๖๔ จาก{" "}
                    <a href="https://vrsiam.org/ภาพจิตรกรรมฝาผนัง-ในวิห/" target="_blank" className="text-[#d8653b] hover:underline">
                        VRSiam.org
                    </a>
                    </p>
                </div>
                </section>

            </article>

            <div className="mt-8">
              <BackToTop />
            </div>
          </main>

          {/* TOC (สารบัญ) */}
          <aside className="lg:col-span-4">
            {/* Mobile: dropdown */}
            <details className="lg:hidden mb-4 bg-white rounded-xl shadow-sm ring-1 ring-black/5">
              <summary className="px-4 py-3 cursor-pointer font-medium">สารบัญ</summary>
              <nav className="px-4 pb-4">
                {SECTIONS.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block py-1.5 text-sm text-[#5b4a3e] hover:text-[#d8653b]"
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </details>

            {/* Desktop: sticky list */}
            <nav className="hidden lg:block sticky top-6 bg-white rounded-2xl shadow-sm ring-1 ring-black/5 p-6">
              <h2 className="text-base font-semibold mb-3">สารบัญ</h2>
              <div className="space-y-1.5">
                {SECTIONS.map((s) => (
                  <Anchor key={s.id} id={s.id}>
                    {s.title}
                  </Anchor>
                ))}
              </div>
              <PrevNext sections={SECTIONS} active={active} />
            </nav>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}